/**
 * Örtlicher Speicher (IndexedDB über Dexie).
 *
 * Teil der Plattformschicht nach ADR 0001: Anwendungscode greift nie direkt auf
 * IndexedDB zu. Beim späteren Wechsel auf Capacitor Filesystem tauscht nur
 * dieses Modul, nicht die Anwendung.
 *
 * Drei Tabellen, weil sie unterschiedlich lange leben:
 *  - `drafts`  – was der Nutzer erfasst hat, fachlich lesbar
 *  - `outbox`  – der Übertragungsauftrag dazu, mit Idempotenzschlüssel
 *  - `blobs`   – Foto- und Tondaten, getrennt, damit ein Lesevorgang auf der
 *                Liste nicht jedes Mal Megabytes aus der Datenbank zieht
 */
import type { Dexie, EntityTable } from "dexie";

/** Sichtbarer Zustand einer Erfassung (ADR 0001: drei Zustände). */
export type OutboxState =
  | "local" // örtlich gespeichert, noch nicht gesendet
  | "sending" // wird übertragen
  | "confirmed" // vom Server bestätigt
  | "failed" // Übertragung gescheitert, wird erneut versucht
  | "blocked" // Endpunkt fehlt im Backend — kein erneuter Versuch
  // Der Vorgang ist beim Server, mindestens ein Anhang aber nicht — und er
  // lässt sich auch nicht mehr nachreichen, weil der Browser den Datensatz
  // bei Platznot geräumt hat. Ein eigener Zustand, weil weder „bestätigt"
  // noch „gescheitert" stimmt: Wiederholen bringt nichts, und der Nutzer muss
  // trotzdem erfahren, dass sein Beweisfoto fehlt.
  | "partial";

/**
 * Fachliche Art einer eingereihten Handlung.
 *
 * Nicht bloß Beiwerk für die Anzeige: In der Warteschlange stehen Einträge
 * nebeneinander, die nichts miteinander zu tun haben — eine Meldung mit drei
 * Fotos neben einem abgehakten Prüfpunkt. „POST /checklist-items/…" sagt einem
 * Hausmeister nichts; die Art sagt ihm, worauf er noch wartet.
 *
 * Bis zum 02.08.2026 gab es hier nur `report`, weil die Warteschlange nur den
 * Meldungsversand kannte. Alles andere lief als unmittelbarer Aufruf und war bei
 * Verbindungsabbruch verloren.
 */
export type DraftKind =
  | "report" // Meldung samt Anhängen
  | "comment" // Beitrag an Vorgang oder Aufgabe
  | "task" // neue Aufgabe
  | "task-change" // Aufgabe geändert
  | "issue-change" // Vorgang geändert (nicht: Status)
  | "stop-visit" // Halt bearbeitet
  | "stop-skip" // Halt übersprungen, mit Begründung
  | "stop-scan" // Etikett am Halt gelesen
  | "checklist-item"; // Prüfpunkt abgehakt

export interface DraftRecord {
  id: string;
  kind: DraftKind;
  createdAt: number;
  /** Kurzfassung für die Anzeige in Listen, ohne den Datensatz zu laden. */
  title: string;
  /** Fachliche Nutzlast, wie sie später an das Backend geht. */
  payload: Record<string, unknown>;
  propertyId: string | null;
  roomId: string | null;
}

export interface OutboxRecord {
  id: string;
  draftId: string | null;
  /**
   * Art und Kurzfassung, **noch einmal** am Übertragungsauftrag.
   *
   * Sie stehen bereits am Entwurf, und eine Kopie ist normalerweise ein Fehler.
   * Hier ist sie Absicht: Die Anzeige der Warteschlange zeigt Einträge, deren
   * Entwurf fehlen kann — IndexedDB räumt bei Platznot, und genau dann ist die
   * Frage „was wartet da eigentlich?" am wichtigsten. Ohne die Kopie stünde
   * dort nur „POST /checklist-items/…", was einem Hausmeister nichts sagt.
   *
   * Fehlt bei Einträgen, die vor dieser Fassung angelegt wurden.
   */
  kind?: DraftKind;
  title?: string;
  /** Zielpfad relativ zum Schnittstellengrundpfad, etwa `/issues`. */
  path: string;
  method: "POST" | "PATCH" | "PUT" | "DELETE";
  body: Record<string, unknown>;
  /**
   * Wird als `Idempotency-Key` gesendet. Erzeugt beim Anlegen, nie danach —
   * darin liegt der Sinn: Ein zweiter Sendeversuch nach Verbindungsabbruch soll
   * keinen zweiten Vorgang erzeugen (Entscheidung E-Z-04).
   *
   * **Wo er wirkt.** Seit dem 02.08.2026 werten ihn die Endpunkte aus, die aus
   * der Warteschlange Datensätze anlegen: `POST /issues`,
   * `POST /issues/{id}/comments`, `POST /tasks`, `POST /tasks/{id}/comments`
   * (dazu `reports.py` und `ai.py` wie bisher). Die übrigen eingereihten
   * Handlungen brauchen ihn nicht — ein `PATCH` setzt Felder, und die
   * Rundgangsaufrufe schreiben an einem vorhandenen Halt; ein zweiter Anlauf
   * ergibt denselben Endzustand.
   *
   * **`POST /media` wertet ihn weiterhin nicht aus.** Dort läge der Körper als
   * Multipart vor, und `IdempotencyGuard.check` müsste ihn vollständig einlesen
   * — die Datei wäre danach ein zweites Mal zu lesen. Gegen doppelte Anhänge
   * hilft stattdessen das Aufräumen im Gerät: Jeder Anhang wird gelöscht,
   * sobald er angekommen ist (siehe `sendeAnhaenge` in `lib/queue.ts`).
   */
  idempotencyKey: string;
  /**
   * Kennung des angelegten Datensatzes, sobald der Server geantwortet hat.
   *
   * Der Grund ist ein doppelter Vorgang: Scheitert nach dem erfolgreichen
   * Anlegen nur noch ein Anhang, war der bisherige Wiederholversuch ein
   * vollständiger zweiter Aufruf desselben Pfades. Steht hier eine Kennung,
   * wird der Vorgang nicht noch einmal geschickt, sondern nur der Rest.
   *
   * Bleibt auch neben dem inzwischen wirksamen `Idempotency-Key` sinnvoll: Der
   * Schlüssel lässt den zweiten Aufruf laufen und gibt die gespeicherte Antwort
   * zurück, `serverId` erspart den Aufruf ganz. Auf einer knappen Verbindung
   * ist das der Unterschied zwischen einer Anfrage und keiner.
   *
   * Fehlt bei Einträgen, die vor dieser Fassung angelegt wurden.
   */
  serverId?: string | null;
  state: OutboxState;
  attempts: number;
  createdAt: number;
  /** Frühester nächster Versuch als Zeitstempel in Millisekunden. */
  nextAttemptAt: number;
  lastError: string | null;
  /**
   * Hinweise des Servers zu den Anhängen, in Anzeigeform.
   *
   * Der Online-Weg zeigt sie als Kurzmeldung an (`routes/media.tsx`). Aus der
   * Warteschlange heraus geht das nicht — sie läuft im Hintergrund, oft ohne
   * offene Maske. Deshalb bleiben die Hinweise am Eintrag stehen, bis der
   * Nutzer ihn gesehen und verworfen hat.
   *
   * Fehlt bei Einträgen, die vor dieser Fassung angelegt wurden.
   */
  notices?: string[];
  /** Kennungen der zugehörigen Anhänge in `blobs`. */
  blobIds: string[];
}

export type BlobKind = "photo" | "audio" | "photo-original";

export interface BlobRecord {
  id: string;
  draftId: string;
  kind: BlobKind;
  mime: string;
  filename: string;
  bytes: number;
  data: Blob;
  /** Aufnahmezeit, Ausrichtung, Ursprungsgröße — siehe src/lib/image.ts. */
  meta: Record<string, string | number | null>;
  createdAt: number;
}

export type BauSeppDb = Dexie & {
  drafts: EntityTable<DraftRecord, "id">;
  outbox: EntityTable<OutboxRecord, "id">;
  blobs: EntityTable<BlobRecord, "id">;
};

let opening: Promise<BauSeppDb> | null = null;

/**
 * Öffnet die Datenbank; beim ersten Aufruf wird Dexie nachgeladen.
 *
 * Die Rückgabe ist ein Versprechen, obwohl das jeden Aufrufer zu `await` zwingt.
 * Grund: Dexie sind rund 28 kB komprimiert. Im Startbündel wäre das ein Sechstel
 * der Obergrenze aus ADR 0001 (200 kB) — für eine Bibliothek, die erst bei der
 * ersten Erfassung gebraucht wird. Nach dem ersten Aufruf ist das Versprechen
 * bereits erfüllt und kostet nichts mehr.
 */
export function db(): Promise<BauSeppDb> {
  opening ??= (async () => {
    const { default: Dexie } = await import("dexie");
    const database = new Dexie("bausepp") as BauSeppDb;
    database.version(1).stores({
      drafts: "id, kind, createdAt, propertyId, roomId",
      // Zusammengesetzter Index auf Zustand und Fälligkeit: Die Warteschlange
      // fragt genau danach („was ist offen und jetzt dran"), sonst müsste sie
      // bei jedem Durchlauf die ganze Tabelle durchsehen.
      outbox: "id, draftId, state, [state+nextAttemptAt], createdAt",
      blobs: "id, draftId, kind",
    });
    return database;
  })();
  return opening;
}

/** Neue Kennung. Über die Plattformschicht, damit Tests sie ersetzen können. */
export function newId(): string {
  return crypto.randomUUID();
}

/**
 * Verfügbaren Speicher abschätzen. Vor einem Rundgang mit vierzig Fotos ist das
 * die Angabe, die zählt — läuft der Speicher voll, verwirft der Browser die
 * Datenbank, und zwar ohne Rückfrage.
 */
export async function storageEstimate(): Promise<{ usedBytes: number; quotaBytes: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const estimate = await navigator.storage.estimate();
  return { usedBytes: estimate.usage ?? 0, quotaBytes: estimate.quota ?? 0 };
}

/**
 * Bittet den Browser, den Speicher dauerhaft zu halten. Ohne das darf er ihn
 * bei Platznot löschen — mit einer noch nicht übertragenen Meldung darin.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted()) return true;
  return navigator.storage.persist();
}
